// src/pages/ExpensesPage.tsx
import React, { useCallback, useMemo, useState } from "react";
import { useData } from "../DataContext";
import { generateId } from "../utils/id";
import { Expense } from "../data/mockData";
import {
  projectDisplayLabelPretty,
  subContractDisplayLabelPretty,
} from "../utils/format";

import { Card } from "../components/ui/Card";
import { useTranslation } from "react-i18next";
import { TableShell, CellClamp } from "../components/ui/TableShell";

type ExpenseType = "Expense" | "Sub-Contract Payment";
type PaymentMethod = "Cash" | "Bank";

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Lightweight inline icons (no deps)
function IconPencil({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconTrash({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 6l1 16h8l1-16" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function getProjectIdFromRow(row: any): string {
  return (row?.projectId ?? row?.project_id ?? "").toString();
}

export default function ExpensesPage() {
  const { t } = useTranslation();
  const {
    clients,
    projects,
    expenses,
    addExpense,
    updateExpense,
    deleteExpense,
    subContracts,
    subContractors,
    partners,

    // added as fallback source for subcontractor names
    resources,
  } = useData() as any;

  // -------------------------
  // Add form state
  // -------------------------
  const [projectId, setProjectId] = useState("");
  const [expenseType, setExpenseType] = useState<ExpenseType>("Expense");
  const [whoPaid, setWhoPaid] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("Cash");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [subContractId, setSubContractId] = useState<string>("");

  const [formError, setFormError] = useState<string | null>(null);

  // -------------------------
  // Editing state
  // -------------------------
  const [editingId, setEditingId] = useState<string | null>(null);

  const [editProjectId, setEditProjectId] = useState("");
  const [editExpenseType, setEditExpenseType] = useState<ExpenseType>("Expense");
  const [editWhoPaid, setEditWhoPaid] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editPaymentMethod, setEditPaymentMethod] = useState<PaymentMethod>("Cash");
  const [editDate, setEditDate] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editSubContractId, setEditSubContractId] = useState<string>("");

  // -------------------------
  // Controls (Search top-right + filters in ledger card)
  // -------------------------
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<ExpenseType | "All">("All");
  const [methodFilter, setMethodFilter] = useState<PaymentMethod | "All">("All");

  // -------------------------
  // Helpers
  // -------------------------
  const whoPaidOptions = useMemo(() => {
    const base = ["Company", ...(partners || []).map((p: any) => p.name)];
    return Array.from(new Set(base)).filter(Boolean);
  }, [partners]);

  const projectLabel = useCallback(
    (pid: string) => {
      const p = (projects || []).find((x: any) => x.id === pid);
      return p ? projectDisplayLabelPretty(p, clients) : pid;
    },
    [projects, clients]
  );

  // build subcontractor list for pretty labels (prefer subContractors; fallback to resources)
  const dbSubcontractors = useMemo(() => {
    const list: any[] = [];

    if (Array.isArray(subContractors) && subContractors.length) {
      subContractors.forEach((c: any) => {
        if (!c?.id) return;
        list.push({ id: c.id, name: c.name || c.displayName || c.id });
      });
      return list;
    }

    // fallback: derive from resources
    (resources || [])
      .filter((r: any) => Number(r.isSubcontractor ?? r.is_subcontractor ?? 0) === 1)
      .forEach((r: any) => {
        if (!r?.id) return;
        list.push({ id: r.id, name: r.name || r.id });
      });

    return list;
  }, [subContractors, resources]);

  const subcontractOptionsForProject = useMemo(() => {
    if (!projectId) return [];
    return (subContracts || []).filter((sc: any) => getProjectIdFromRow(sc) === projectId);
  }, [subContracts, projectId]);

  const subcontractOptionsForEdit = useMemo(() => {
    if (!editProjectId) return [];
    return (subContracts || []).filter((sc: any) => getProjectIdFromRow(sc) === editProjectId);
  }, [subContracts, editProjectId]);

  // Use EXISTING pretty subcontract formatter you already created
  const getSubContractPrettyLabel = useCallback(
    (subId?: string) => {
      if (!subId) return "-";
      const sc: any = (subContracts || []).find((x: any) => x.id === subId);
      if (!sc) return subId;
      return subContractDisplayLabelPretty(sc, projects, clients, dbSubcontractors);
    },
    [subContracts, projects, clients, dbSubcontractors]
  );

  const resetAddForm = () => {
    setProjectId("");
    setExpenseType("Expense");
    setWhoPaid("");
    setAmount("");
    setPaymentMethod("Cash");
    setDate("");
    setNotes("");
    setSubContractId("");
    setFormError(null);
  };

  // -------------------------
  // Add / Edit handlers
  // -------------------------
  const handleAdd = () => {
    const amt = Number(amount);
    const mustHaveSub = expenseType === "Sub-Contract Payment";

    if (!projectId || !whoPaid || !amount || !paymentMethod || !date) {
      setFormError("Please fill all required fields.");
      return;
    }
    if (Number.isNaN(amt) || amt <= 0) {
      setFormError("Amount must be a valid number greater than 0.");
      return;
    }
    if (mustHaveSub && !subContractId) {
      setFormError("Please select a Sub-Contract for a Sub-Contract Payment.");
      return;
    }

    addExpense({
      id: generateId("EXP"),
      projectId,
      expenseType,
      whoPaid,
      amount: amt,
      paymentMethod,
      date,
      notes: notes.trim() ? notes.trim() : undefined,
      subContractId: mustHaveSub ? subContractId : undefined,
    } as Expense);

    resetAddForm();
  };

  const startEdit = (e: Expense) => {
    const ee: any = e;
    setEditingId(ee.id);
    setEditProjectId(ee.projectId);
    setEditExpenseType(ee.expenseType as ExpenseType);
    setEditWhoPaid(ee.whoPaid || "");
    setEditAmount(String(ee.amount ?? ""));
    setEditPaymentMethod((ee.paymentMethod as PaymentMethod) || "Cash");
    setEditDate(ee.date || "");
    setEditNotes(ee.notes || "");
    setEditSubContractId(ee.subContractId || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditProjectId("");
    setEditExpenseType("Expense");
    setEditWhoPaid("");
    setEditAmount("");
    setEditPaymentMethod("Cash");
    setEditDate("");
    setEditNotes("");
    setEditSubContractId("");
  };

  const saveEdit = (e: Expense) => {
    const ee: any = e;
    const amt = Number(editAmount);
    const mustHaveSub = editExpenseType === "Sub-Contract Payment";

    if (!editProjectId || !editWhoPaid || !editAmount || !editPaymentMethod || !editDate) {
      alert(t("expenses.requiredFields"));
      return;
    }
    if (Number.isNaN(amt) || amt <= 0) {
      alert(t("expenses.amountInvalid"));
      return;
    }
    if (mustHaveSub && !editSubContractId) {
      alert(t("expenses.subContractRequired"));
      return;
    }

    updateExpense({
      ...ee,
      projectId: editProjectId,
      expenseType: editExpenseType,
      whoPaid: editWhoPaid,
      amount: amt,
      paymentMethod: editPaymentMethod,
      date: editDate,
      notes: editNotes.trim() ? editNotes.trim() : undefined,
      subContractId: mustHaveSub ? editSubContractId : undefined,
    });

    cancelEdit();
  };

  const doDelete = (e: Expense) => {
    const ee: any = e;
    const ok = window.confirm(t("expenses.deleteConfirm", { type: ee.expenseType, amount: money(ee.amount), date: ee.date }));
    if (!ok) return;

    deleteExpense(ee.id);
    if (editingId === ee.id) cancelEdit();
  };

  // -------------------------
  // Visible list (filters + search + newest first)
  // -------------------------
  const visibleExpenses = useMemo(() => {
    const q = search.trim().toLowerCase();

    return [...(expenses || [])]
      .filter((e: any) => {
        if (typeFilter !== "All" && (e.expenseType as ExpenseType) !== typeFilter) return false;
        if (methodFilter !== "All" && (e.paymentMethod as PaymentMethod) !== methodFilter) return false;

        if (!q) return true;

        const p = (projects || []).find((x: any) => x.id === e.projectId);

        const hay = [
          // removed expense id from UI later, but keep in search
          e.id,
          projectLabel(e.projectId),
          e.date,
          e.expenseType,
          e.whoPaid,
          e.paymentMethod,
          e.notes || "",
          p?.name || "",
          p?.address || "",
          getSubContractPrettyLabel(e.subContractId),
        ]
          .join(" ")
          .toLowerCase();

        return hay.includes(q);
      })
      .sort((a: any, b: any) => (a.date < b.date ? 1 : -1));
  }, [expenses, projects, search, typeFilter, methodFilter, projectLabel, getSubContractPrettyLabel]);

  // -------------------------
  // Totals (FILTERED like SubContracts/Payments pages)
  // -------------------------
  const filteredTotals = useMemo(() => {
    const trueCost = visibleExpenses
      .filter((e: any) => e.expenseType === "Expense" || e.expenseType === "Sub-Contract Payment")
      .reduce((s: number, e: any) => s + (Number(e.amount) || 0), 0);

    const cash = visibleExpenses
      .filter((e: any) => e.paymentMethod === "Cash")
      .reduce((s: number, e: any) => s + (Number(e.amount) || 0), 0);

    const bank = visibleExpenses
      .filter((e: any) => e.paymentMethod === "Bank")
      .reduce((s: number, e: any) => s + (Number(e.amount) || 0), 0);

    return { trueCost, cash, bank };
  }, [visibleExpenses]);

  // Ledger header-right controls (filters inside the ledger card)
  const ledgerHeaderRight = (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "end",
        justifyContent: "flex-end",
      }}
    >
      <div style={{ minWidth: 220 }}>
        <label className="label" style={{ marginBottom: 6 }}>
          Type
        </label>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as any)}
          style={{ width: "100%" }}
        >
          <option value="All">{t("common.all")}</option>
          <option value="Expense">{t("common.expense")}</option>
          <option value="Sub-Contract Payment">Sub-Contract Payment</option>
        </select>
      </div>

      <div style={{ minWidth: 170 }}>
        <label className="label" style={{ marginBottom: 6 }}>
          Method
        </label>
        <select
          value={methodFilter}
          onChange={(e) => setMethodFilter(e.target.value as any)}
          style={{ width: "100%" }}
        >
          <option value="All">{t("common.all")}</option>
          <option value="Cash">{t("common.cash")}</option>
          <option value="Bank">{t("common.bank")}</option>
        </select>
      </div>

      {(typeFilter !== "All" || methodFilter !== "All") && (
        <button
          className="btn btnSecondary btnSm"
          type="button"
          onClick={() => {
            setTypeFilter("All");
            setMethodFilter("All");
          }}
          title={t("expenses.clearFiltersBtn")}
        >
          Clear
        </button>
      )}
    </div>
  );

  return (
    <div className="container">
      {/* Page-scoped fixes */}
      <style>{`
        /* Responsive column hiding */
        @media (max-width: 980px) {
          .expHideMd { display: none !important; }
          .expActionsTd { min-width: 140px; }
          .expRow5 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 680px) {
          .expHideSm { display: none !important; }
          .expActionsTd { min-width: 120px; }
          .expRow5 { grid-template-columns: 1fr !important; }
        }

        /* Ensure grid items don't overflow their columns */
        .expField { min-width: 0; }

        textarea.input {
          box-sizing: border-box;
          width: 100%;
          max-width: 100%;
        }

        /* Modern table action icon buttons */
        .tableIconBtn {
          height: 36px;
          width: 36px;
          display: grid;
          place-items: center;
          border-radius: 999px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          color: var(--text);
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
          transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
        }
        .tableIconBtn:hover {
          background: rgba(255,255,255,1);
          transform: translateY(-1px);
          border-color: rgba(15, 23, 42, 0.18);
        }
        .tableIconBtn:active { transform: translateY(0px); }

        .tableIconBtnDanger {
          border-color: rgba(229, 83, 61, 0.28);
          background: rgba(229, 83, 61, 0.08);
          color: var(--bad);
          box-shadow: none;
        }
        .tableIconBtnDanger:hover {
          background: rgba(229, 83, 61, 0.12);
          border-color: rgba(229, 83, 61, 0.40);
        }

        /* Ledger typography tightened */
        .expensesLedger table { font-size: 13px; }
        .expensesLedger thead th { padding: 10px 12px; }
        .expensesLedger tbody td { padding: 10px 12px; }
      `}</style>

      {/* Header: title left, SEARCH top-right */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>Expenses</h1>
          <div className="pageNote">
            {t("expenses.pageNote")}
          </div>
        </div>

        <div style={{ minWidth: 320, maxWidth: 520, flex: "1 1 360px" }}>
          <label className="label">{t("common.search")}</label>
          <input
            className="input"
            placeholder={t("expenses.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {/* ADD EXPENSE */}
      <Card
        title={t("expenses.addTitle")}
        subtitle={
          <>
            <span className="reqStar">*</span> Required
          </>
        }
      >
        <div style={{ display: "grid", gap: 14 }}>
          {/* Project full width */}
          <div className="field expField">
            <label className="label">
              Project <span className="reqStar">*</span>
            </label>
            <select
              value={projectId}
              onChange={(e) => {
                const next = e.target.value;
                setProjectId(next);
                setSubContractId("");
                if (formError) setFormError(null);
              }}
              style={{ width: "100%" }}
            >
              <option value="">{t("expenses.selectProject")}</option>
              {projects.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {projectLabel(p.id)}
                </option>
              ))}
            </select>
          </div>

          {/* Row: 5 across on desktop */}
          <div
            className="expRow5"
            style={{
              display: "grid",
              gap: 14,
              gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
              alignItems: "end",
            }}
          >
            <div className="field expField">
              <label className="label">
                {t("expenses.typeLabel")} <span className="reqStar">*</span>
              </label>
              <select
                value={expenseType}
                onChange={(e) => {
                  const next = e.target.value as ExpenseType;
                  setExpenseType(next);
                  if (next !== "Sub-Contract Payment") setSubContractId("");
                  if (formError) setFormError(null);
                }}
                style={{ width: "100%" }}
              >
                <option value="Expense">{t("common.expense")}</option>
                <option value="Sub-Contract Payment">Sub-Contract Payment</option>
              </select>
            </div>

            <div className="field expField">
              <label className="label">
                {t("expenses.amountLabel")} <span className="reqStar">*</span>
              </label>
              <input
                className="input"
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  if (formError) setFormError(null);
                }}
                style={{ width: "100%" }}
              />
            </div>

            <div className="field expField">
              <label className="label">
                {t("expenses.dateLabel")} <span className="reqStar">*</span>
              </label>
              <input
                className="input"
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  if (formError) setFormError(null);
                }}
                style={{ width: "100%" }}
              />
            </div>

            <div className="field expField">
              <label className="label">
                {t("expenses.whoPaidLabel")} <span className="reqStar">*</span>
              </label>
              <select
                value={whoPaid}
                onChange={(e) => {
                  setWhoPaid(e.target.value);
                  if (formError) setFormError(null);
                }}
                style={{ width: "100%" }}
              >
                <option value="">{t("expenses.selectPayer")}</option>
                {whoPaidOptions.map((x: string) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>

            <div className="field expField">
              <label className="label">
                {t("expenses.methodLabel")} <span className="reqStar">*</span>
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value as PaymentMethod);
                  if (formError) setFormError(null);
                }}
                style={{ width: "100%" }}
              >
                <option value="Cash">{t("common.cash")}</option>
                <option value="Bank">{t("common.bank")}</option>
              </select>
            </div>
          </div>

          {/* Subcontract selector */}
          {expenseType === "Sub-Contract Payment" && (
            <div className="field expField">
              <label className="label">
                {t("expenses.subContractLabel")} <span className="reqStar">*</span>
              </label>
              <select
                value={subContractId}
                onChange={(e) => {
                  setSubContractId(e.target.value);
                  if (formError) setFormError(null);
                }}
                style={{ width: "100%" }}
                disabled={!projectId}
              >
                <option value="">{projectId ? t("expenses.selectSubContract") : t("expenses.selectProjectFirst")}</option>
                {subcontractOptionsForProject.map((sc: any) => (
                  <option key={sc.id} value={sc.id}>
                    {getSubContractPrettyLabel(sc.id)}
                  </option>
                ))}
              </select>

              <div className="noteOnCard" style={{ marginTop: 6 }}>
                {t("expenses.subContractNote")}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="field expField">
            <label className="label">{t("common.notes")}</label>
            <textarea
              className="input"
              placeholder={t("expenses.notesPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ minHeight: 86, resize: "vertical" }}
            />
          </div>
        </div>

        {formError && (
          <div className="formError" role="alert">
            {formError}
          </div>
        )}

        <div className="formActions">
          <button className="btn" onClick={handleAdd} type="button">{t("expenses.addBtn")}</button>
          <button className="btn btnSecondary" onClick={resetAddForm} type="button">{t("common.clear")}</button>
        </div>
      </Card>

      {/* TOTALS */}
      <div className="card" style={{ padding: 12, marginTop: 14, marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontWeight: 1000 }}>
            {t("expenses.totalsFiltered")}<span className="badge" style={{ marginLeft: 8 }} title={t("expenses.filterTip")}>
              {visibleExpenses.length} {t("common.rows")}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 14,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              fontSize: 13,
            }}
          >
            <span style={{ color: "var(--muted)" }}>
              {t("expenses.trueCost")} <b className="numBad">{money(filteredTotals.trueCost)}</b>
            </span>
            <span style={{ color: "var(--muted)" }}>
              {t("expenses.cashPaid")} <b className="numWarn">{money(filteredTotals.cash)}</b>
            </span>
            <span style={{ color: "var(--muted)" }}>
              {t("expenses.bankPaid")} <b className="numWarn">{money(filteredTotals.bank)}</b>
            </span>
          </div>
        </div>

        <div className="noteOnCard" style={{ marginTop: 8 }}>
          {t("expenses.partnerNote")}
        </div>
      </div>

      {/* LEDGER */}
      <div className="expensesLedger">
        <TableShell
          title={t("expenses.ledgerTitle")}
          subtitle={`${visibleExpenses.length} ${t("common.rows")} • ${t("expenses.ledgerSubtitle").split("rows • ")[1] || "newest first"}`}
          right={ledgerHeaderRight}
        >
          <table>
            <thead>
              <tr>
                {/* Project column removed */}
                <th style={{ width: 120 }}>{t("common.date")}</th>
                <th style={{ width: 360 }}>{t("expenses.colTypeProject")}</th>
                <th style={{ width: 120, textAlign: "right" }}>{t("common.amount")}</th>
                <th className="expHideMd" style={{ width: 160 }}>
                  {t("expenses.colWho")}
                </th>
                <th className="expHideSm" style={{ width: 110 }}>
                  {t("common.method")}
                </th>
                <th className="expHideMd" style={{ width: 320 }}>
                  {t("expenses.colSubContract")}
                </th>
                <th style={{ width: 220 }}>{t("common.notes")}</th>
                <th style={{ width: 140 }}>{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {visibleExpenses.map((e: any) => {
                const isEditing = editingId === e.id;
                const effectiveType = isEditing ? editExpenseType : (e.expenseType as ExpenseType);

                return (
                  <tr key={e.id}>
                    <td>
                      {isEditing ? (
                        <input
                          className="input"
                          type="date"
                          value={editDate}
                          onChange={(ev) => setEditDate(ev.target.value)}
                        />
                      ) : (
                        e.date
                      )}
                    </td>
                    
                    <td>
                      {isEditing ? (
                        <div style={{ display: "grid", gap: 8 }}>
                          {/* Project select */}
                          <div>
                            <label className="label" style={{ marginBottom: 6 }}>
                              Project
                            </label>
                            <select
                              value={editProjectId}
                              onChange={(ev) => {
                                const next = ev.target.value;
                                setEditProjectId(next);
                                setEditSubContractId("");
                              }}
                              style={{ width: "100%" }}
                            >
                              <option value="">{t("expenses.selectProject")}</option>
                              {projects.map((p: any) => (
                                <option key={p.id} value={p.id}>
                                  {projectLabel(p.id)}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Type select */}
                          <div>
                            <label className="label" style={{ marginBottom: 6 }}>
                              Type
                            </label>
                            <select
                              value={editExpenseType}
                              onChange={(ev) => {
                                const next = ev.target.value as ExpenseType;
                                setEditExpenseType(next);
                                if (next !== "Sub-Contract Payment") setEditSubContractId("");
                              }}
                              style={{ width: "100%" }}
                            >
                              <option value="Expense">{t("common.expense")}</option>
                              <option value="Sub-Contract Payment">Sub-Contract Payment</option>
                            </select>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 6 }}>
                          <div>
                            {e.expenseType === "Sub-Contract Payment" ? (
                              <span className="badge badgeWarn">{t("expenses.subPaymentBadge")}</span>
                            ) : (
                              <span className="badge">{t("expenses.expenseBadge")}</span>
                            )}
                          </div>

                          {/* Pretty project under the type */}
                          <CellClamp lines={2} title={projectLabel(e.projectId)}>
                            <span style={{ fontWeight: 900 }}>{projectLabel(e.projectId)}</span>
                          </CellClamp>
                        </div>
                      )}
                    </td>



                    <td style={{ textAlign: "right" }}>
                      {isEditing ? (
                        <input
                          className="input"
                          type="number"
                          value={editAmount}
                          onChange={(ev) => setEditAmount(ev.target.value)}
                          style={{ width: 120 }}
                        />
                      ) : (
                        <span className="numBad">{money(e.amount)}</span>
                      )}
                    </td>

                    <td className="expHideMd">
                      {isEditing ? (
                        <select
                          value={editWhoPaid}
                          onChange={(ev) => setEditWhoPaid(ev.target.value)}
                          style={{ width: "100%" }}
                        >
                          <option value="">{t("expenses.selectPayer")}</option>
                          {whoPaidOptions.map((x: string) => (
                            <option key={x} value={x}>
                              {x}
                            </option>
                          ))}
                        </select>
                      ) : (
                        e.whoPaid
                      )}
                    </td>

                    <td className="expHideSm">
                      {isEditing ? (
                        <select
                          value={editPaymentMethod}
                          onChange={(ev) => setEditPaymentMethod(ev.target.value as PaymentMethod)}
                          style={{ width: "100%" }}
                        >
                          <option value="Cash">{t("common.cash")}</option>
                          <option value="Bank">{t("common.bank")}</option>
                        </select>
                      ) : (
                        <span className="badge">{e.paymentMethod}</span>
                      )}
                    </td>

                    <td className="expHideMd">
                      {isEditing ? (
                        effectiveType === "Sub-Contract Payment" ? (
                          <select
                            value={editSubContractId}
                            onChange={(ev) => setEditSubContractId(ev.target.value)}
                            style={{ width: "100%" }}
                            disabled={!editProjectId}
                          >
                            <option value="">
                              {editProjectId ? t("expenses.selectSubContract") : t("expenses.selectProjectFirst")}
                            </option>
                            {subcontractOptionsForEdit.map((sc: any) => (
                              <option key={sc.id} value={sc.id}>
                                {getSubContractPrettyLabel(sc.id)}
                              </option>
                            ))}
                          </select>
                        ) : (
                          "-"
                        )
                      ) : e.expenseType === "Sub-Contract Payment" ? (
                        <CellClamp lines={2} title={getSubContractPrettyLabel(e.subContractId)}>
                          {getSubContractPrettyLabel(e.subContractId)}
                        </CellClamp>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          className="input"
                          value={editNotes}
                          onChange={(ev) => setEditNotes(ev.target.value)}
                          style={{ width: "100%" }}
                          placeholder={t("expenses.optionalNotes")}
                        />
                      ) : (
                        <CellClamp lines={2} title={e.notes || ""}>
                          {e.notes || "-"}
                        </CellClamp>
                      )}
                    </td>

                    <td className="expActionsTd">
                      {isEditing ? (
                        <div className="actionsRow">
                          <button className="btn btnSm" onClick={() => saveEdit(e)} type="button">{t("common.save")}</button>
                          <button className="btn btnSecondary btnSm" onClick={cancelEdit} type="button">{t("common.cancel")}</button>
                        </div>
                      ) : (
                        <div className="actionsRow" style={{ gap: 10 }}>
                          <button
                            type="button"
                            className="tableIconBtn"
                            onClick={() => startEdit(e)}
                            title={t("expenses.editBtn")}
                            aria-label={t("expenses.editAriaLabel")}
                          >
                            <IconPencil />
                          </button>

                          <button
                            type="button"
                            className="tableIconBtn tableIconBtnDanger"
                            onClick={() => doDelete(e)}
                            title={t("expenses.deleteBtn")}
                            aria-label={t("expenses.deleteAriaLabel")}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {visibleExpenses.length === 0 && (
                <tr>
                  {/* column count updated (now 9 columns) */}
                  <td colSpan={8} style={{ padding: 14, color: "var(--muted)", fontStyle: "italic" }}>
                    {t("expenses.noExpenses")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableShell>

        <div className="noteOnCard" style={{ marginTop: 10 }}>
          Tip: If you’re paying a subcontractor against a specific agreement, use <b>Sub-Contract Payment</b> so balances
          reconcile properly.
        </div>
      </div>
    </div>
  );
}


